/**
 * Folder Tree Helpers
 *
 * Utilities for manipulating folder tree structures.
 * Used by folder mutations and components for optimistic updates.
 *
 * @module features/organization/utils/folderTreeHelpers
 */

import type { FolderTreeNode, FolderUpdateRequest } from '@loader/shared-types';

/**
 * Add a folder to the tree at the correct position
 */
export function addFolderToTree(
  nodes: FolderTreeNode[],
  folder: FolderTreeNode,
  parentId: string | null
): FolderTreeNode[] {
  if (parentId === null) {
    return [...nodes, folder];
  }
  return nodes.map((node) => {
    if (node.id === parentId) {
      return {
        ...node,
        children: [...(node.children || []), folder],
      };
    }
    if (node.children && node.children.length > 0) {
      return {
        ...node,
        children: addFolderToTree(node.children, folder, parentId),
      };
    }
    return node;
  });
}

/**
 * Update a folder in the tree recursively
 */
export function updateFolderInTree(
  nodes: FolderTreeNode[],
  folderId: string,
  updates: FolderUpdateRequest
): FolderTreeNode[] {
  return nodes.map((node) => {
    if (node.id === folderId) {
      return {
        ...node,
        ...(updates.name !== undefined && { name: updates.name }),
        ...(updates.color !== undefined && { color: updates.color }),
      };
    }
    if (node.children && node.children.length > 0) {
      return {
        ...node,
        children: updateFolderInTree(node.children, folderId, updates),
      };
    }
    return node;
  });
}

/**
 * Remove a folder from the tree recursively
 */
export function removeFolderFromTree(
  nodes: FolderTreeNode[],
  idToRemove: string
): FolderTreeNode[] {
  return nodes
    .filter((node) => node.id !== idToRemove)
    .map((node) => ({
      ...node,
      children: node.children
        ? removeFolderFromTree(node.children, idToRemove)
        : [],
    }));
}

/**
 * Find a folder in the tree by ID
 */
export function findFolderInTree(
  nodes: FolderTreeNode[],
  folderId: string
): FolderTreeNode | null {
  for (const node of nodes) {
    if (node.id === folderId) return node;
    if (node.children?.length) {
      const found = findFolderInTree(node.children, folderId);
      if (found) return found;
    }
  }
  return null;
}

/**
 * Move a folder to a new parent in the tree
 * Removes from current position and adds to new parent
 */
export function moveFolderInTree(
  nodes: FolderTreeNode[],
  folderId: string,
  newParentId: string | null
): FolderTreeNode[] {
  // First, find the folder to move (with its children)
  const folderToMove = findFolderInTree(nodes, folderId);
  if (!folderToMove) return nodes;

  // Create updated folder with new parent_folder_id
  const updatedFolder: FolderTreeNode = {
    ...folderToMove,
    parent_folder_id: newParentId,
  };

  // Remove folder from its current position
  const treeWithoutFolder = removeFolderFromTree(nodes, folderId);

  // Add folder to new position
  return addFolderToTree(treeWithoutFolder, updatedFolder, newParentId);
}

/**
 * Recursively collect all folder IDs from the tree
 */
export function getAllFolderIds(nodes: FolderTreeNode[]): string[] {
  const ids: string[] = [];
  const collectIds = (folders: FolderTreeNode[]) => {
    for (const folder of folders) {
      ids.push(folder.id);
      if (folder.children?.length) {
        collectIds(folder.children);
      }
    }
  };
  collectIds(nodes);
  return ids;
}

/**
 * Check if a folder is a descendant of another folder
 */
export function isDescendant(parent: FolderTreeNode, childId: string): boolean {
  if (!parent.children) return false;
  for (const child of parent.children) {
    if (child.id === childId) return true;
    if (isDescendant(child, childId)) return true;
  }
  return false;
}

/**
 * Get children of a parent folder (or root level folders if parentId is null)
 */
export function getChildrenOfParent(
  folders: FolderTreeNode[],
  parentId: string | null
): FolderTreeNode[] {
  if (parentId === null) {
    return folders;
  }
  const parent = findFolderInTree(folders, parentId);
  return parent?.children || [];
}

/**
 * Check if a folder name already exists in a target parent
 */
export function checkNameConflict(
  folders: FolderTreeNode[],
  folderName: string,
  targetParentId: string | null
): boolean {
  const siblings = getChildrenOfParent(folders, targetParentId);
  return siblings.some((f) => f.name.toLowerCase() === folderName.toLowerCase());
}
